import { ref, watch, toValue, onScopeDispose } from "vue";
import { parseUid } from "../utils/uid-generator";

/**
 * Composable for managing expand/collapse state of list items.
 * Auto-collapses other items when one receives focus (for lists > threshold items).
 * Auto-expands newly added items.
 * Tracks which item currently has focus for visual styling.
 *
 * @param {Object} options
 * @param {Function|Ref} options.items - Reactive items array or getter
 * @param {Function|Ref} options.justAddedIndex - Reactive index of just-added item or getter
 * @param {number} options.threshold - Collapse others when items > threshold (default: 2)
 * @param {string} options.dataAttr - Data attribute name to look for (default: "data-uid")
 */
export function useExpandCollapse(options = {}) {
  const { items, justAddedIndex, threshold = 2, dataAttr = "data-uid" } = options;

  const expandedUids = ref([]);
  const lastActiveUid = ref(null); // Persists: last expanded or focused card
  const focusedUid = ref(null); // Cleared when focus leaves
  const isEditingUid = ref(null); // Only set when an input/select/textarea has focus

  function isExpanded(uid) {
    return expandedUids.value.includes(uid);
  }

  function isActive(uid) {
    // Expanded cards: highlight if it's the last active
    // Collapsed cards: only highlight if there's actual DOM focus (header fields)
    if (isExpanded(uid)) {
      return lastActiveUid.value === uid;
    }
    return focusedUid.value === uid;
  }

  function isEditing(uid) {
    return isEditingUid.value === uid;
  }

  function toggle(uid) {
    const idx = expandedUids.value.indexOf(uid);
    if (idx === -1) {
      // Expanding - also highlight this card
      expandedUids.value.push(uid);
      lastActiveUid.value = uid;
    } else {
      expandedUids.value.splice(idx, 1);
    }
  }

  function collapseAllExcept(uid) {
    expandedUids.value = uid ? [uid] : [];
  }

  function handleFocusIn(event) {
    const list = toValue(items);

    // Convert data attribute name to selector and dataset key
    // e.g., "data-blend-uid" -> "[data-blend-uid]" and "blendUid"
    const selector = `[${dataAttr}]`;
    const datasetKey = dataAttr
      .replace(/^data-/, "")
      .replace(/-([a-z])/g, (_, c) => c.toUpperCase());

    const itemEl = event.target.closest(selector);
    if (!itemEl) return;

    const uid = parseUid(itemEl.dataset[datasetKey]);
    if (!uid) return;

    // Track focused item for visual styling
    lastActiveUid.value = uid;
    focusedUid.value = uid;

    // Only suppress warnings when user is actively editing an input field
    const isInput = event.target.matches("input, select, textarea, [contenteditable]");
    isEditingUid.value = isInput ? uid : null;

    // Collapse all except focused (only when > threshold items)
    if (list.length > threshold && expandedUids.value.length > 0) {
      const current = expandedUids.value;
      const shouldChange = current.length !== 1 || current[0] !== uid;
      if (shouldChange) {
        expandedUids.value = [uid];
      }
    }
  }

  function handleFocusOut(event) {
    // Clear focusedUid when focus leaves the card entirely
    // (lastActiveUid persists for expanded card highlighting)
    const selector = `[${dataAttr}]`;
    const relatedEl = event.relatedTarget?.closest?.(selector);
    if (!relatedEl) {
      focusedUid.value = null;
      isEditingUid.value = null;
    } else {
      // Focus moved to a different card - update focusedUid
      const datasetKey = dataAttr
        .replace(/^data-/, "")
        .replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      const newUid = parseUid(relatedEl.dataset[datasetKey]);
      focusedUid.value = newUid || null;
    }
  }

  function clear() {
    expandedUids.value = [];
    lastActiveUid.value = null;
    focusedUid.value = null;
    isEditingUid.value = null;
  }

  // Auto-expand newly added items and highlight them
  let stopJustAddedWatch = null;
  if (justAddedIndex) {
    stopJustAddedWatch = watch(
      () => toValue(justAddedIndex),
      (newIndex) => {
        const list = toValue(items);
        if (newIndex !== null && newIndex >= 0 && list[newIndex]) {
          const newItem = list[newIndex];
          lastActiveUid.value = newItem._uid;
          if (list.length > threshold) {
            expandedUids.value = [newItem._uid];
          } else if (!expandedUids.value.includes(newItem._uid)) {
            expandedUids.value.push(newItem._uid);
          }
        }
      },
      { immediate: true }
    );
  }

  onScopeDispose(() => {
    stopJustAddedWatch?.();
  });

  return {
    expandedUids,
    lastActiveUid,
    isExpanded,
    isActive,
    isEditing,
    toggle,
    collapseAllExcept,
    handleFocusIn,
    handleFocusOut,
    clear,
  };
}
